import { useState, useEffect } from 'react'
import { AdminUser } from '../lib/authService'
import evangelismService, {
  EvangelismContact,
  ContactStage,
  Interaction,
  ChurchEvangelismStats,
} from '../services/evangelismService'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Users,
  UserPlus,
  Phone,
  Mail,
  Calendar,
  Clock,
  MessageSquare,
  BookOpen,
  Heart,
  AlertCircle,
  CheckCircle,
  Loader2,
  Search,
  Filter,
  BarChart3,
  Award,
  MoreVertical,
  Edit,
  Trash2,
  Plus,
} from 'lucide-react'

interface EvangelismProps {
  user: AdminUser | null
}

export default function Evangelism({ user: currentUser }: EvangelismProps) {
  const [contacts, setContacts] = useState<EvangelismContact[]>([])
  const [evangelists, setEvangelists] = useState<Array<{ id: string, name: string, email: string }>>([])
  const [stats, setStats] = useState<ChurchEvangelismStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [activeTab, setActiveTab] = useState<string>('contacts')

  // Filters
  const [searchTerm, setSearchTerm] = useState('')
  const [stageFilter, setStageFilter] = useState<ContactStage | 'all'>('all')

  // Dialogs
  const [selectedContact, setSelectedContact] = useState<EvangelismContact | null>(null)
  const [showContactDialog, setShowContactDialog] = useState(false)
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [showEditDialog, setShowEditDialog] = useState(false)
  const [showInteractionDialog, setShowInteractionDialog] = useState(false)
  const [showAssignDialog, setShowAssignDialog] = useState(false)
  const [contactInteractions, setContactInteractions] = useState<Interaction[]>([])

  // Forms
  const [newContact, setNewContact] = useState({
    name: '',
    phone: '',
    email: '',
    stage: 'prospect' as ContactStage,
    evangelist_id: '',
    notes: '',
    next_action: '',
    next_action_date: ''
  })

  const [newInteraction, setNewInteraction] = useState({
    type: 'visit' as Interaction['type'],
    notes: '',
    duration_minutes: 0
  })

  const [selectedDiscipler, setSelectedDiscipler] = useState('')

  useEffect(() => {
    if (currentUser?.churchId) {
      loadData()
    }
  }, [currentUser])

  const loadData = async () => {
    if (!currentUser?.churchId) return

    try {
      setLoading(true)
      await Promise.all([
        loadContacts(),
        loadStats(),
        loadEvangelists()
      ])
    } finally {
      setLoading(false)
    }
  }

  const loadContacts = async () => {
    if (!currentUser?.churchId) return

    try {
      const data = await evangelismService.getAllContacts(currentUser.churchId, {
        stage: stageFilter !== 'all' ? stageFilter : undefined,
        search: searchTerm || undefined
      })
      setContacts(data)
    } catch (error) {
      console.error('Error loading contacts:', error)
    }
  }

  const loadStats = async () => {
    if (!currentUser?.churchId) return

    try {
      const data = await evangelismService.getChurchStats(currentUser.churchId)
      setStats(data)
    } catch (error) {
      console.error('Error loading stats:', error)
    }
  }

  const loadEvangelists = async () => {
    if (!currentUser?.churchId) return

    try {
      const data = await evangelismService.getEvangelists(currentUser.churchId)
      setEvangelists(data)
    } catch (error) {
      console.error('Error loading evangelists:', error)
    }
  }

  const handleContactClick = async (contact: EvangelismContact) => {
    setSelectedContact(contact)
    setShowContactDialog(true)

    // Load interactions
    try {
      const interactions = await evangelismService.getContactInteractions(contact.id)
      setContactInteractions(interactions)
    } catch (error) {
      console.error('Error loading interactions:', error)
    }
  }

  const handleCreateContact = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!currentUser?.churchId) return

    setSaving(true)
    try {
      await evangelismService.createContact({
        ...newContact,
        church_id: currentUser.churchId,
        evangelist_id: newContact.evangelist_id || currentUser.id
      })

      setShowCreateDialog(false)
      setNewContact({
        name: '',
        phone: '',
        email: '',
        stage: 'prospect',
        evangelist_id: '',
        notes: '',
        next_action: '',
        next_action_date: ''
      })
      await loadData()
    } catch (error) {
      console.error('Error creating contact:', error)
      alert('Failed to create contact')
    } finally {
      setSaving(false)
    }
  }

  const handleEditContact = async () => {
    if (!selectedContact) return

    setSaving(true)
    try {
      await evangelismService.updateContact(selectedContact.id, {
        name: selectedContact.name,
        phone: selectedContact.phone,
        email: selectedContact.email,
        stage: selectedContact.stage,
        notes: selectedContact.notes,
        next_action: selectedContact.next_action,
        next_action_date: selectedContact.next_action_date
      })

      setShowEditDialog(false)
      await loadData()
    } catch (error) {
      console.error('Error updating contact:', error)
      alert('Failed to update contact')
    } finally {
      setSaving(false)
    }
  }

  const handleRecordInteraction = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedContact || !currentUser?.id) return

    setSaving(true)
    try {
      await evangelismService.recordInteraction({
        contact_id: selectedContact.id,
        evangelist_id: currentUser.id,
        type: newInteraction.type,
        notes: newInteraction.notes,
        duration_minutes: newInteraction.duration_minutes || undefined,
        outcome: undefined
      })

      setShowInteractionDialog(false)
      setNewInteraction({
        type: 'visit',
        notes: '',
        duration_minutes: 0
      })

      // Reload contact interactions
      const interactions = await evangelismService.getContactInteractions(selectedContact.id)
      setContactInteractions(interactions)
      await loadStats()
    } catch (error) {
      console.error('Error recording interaction:', error)
      alert('Failed to record interaction')
    } finally {
      setSaving(false)
    }
  }

  const handleStageChange = async (contactId: string, newStage: ContactStage) => {
    try {
      await evangelismService.updateContactStage(contactId, newStage)
      await loadData()
    } catch (error) {
      console.error('Error updating stage:', error)
      alert('Failed to update stage')
    }
  }

  const handleAssignDiscipler = async () => {
    if (!selectedContact || !selectedDiscipler || !currentUser?.churchId) return

    setSaving(true)
    try {
      await evangelismService.assignDiscipler(
        selectedContact.id,
        selectedDiscipler,
        currentUser.churchId,
        selectedContact.evangelist_id,
        'Admin assigned discipler'
      )

      setShowAssignDialog(false)
      setSelectedDiscipler('')
      await loadData()
    } catch (error) {
      console.error('Error assigning discipler:', error)
      alert('Failed to assign discipler')
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteContact = async (contactId: string) => {
    if (!confirm('Are you sure you want to delete this contact? This action cannot be undone.')) {
      return
    }

    try {
      await evangelismService.deleteContact(contactId)
      await loadData()
    } catch (error) {
      console.error('Error deleting contact:', error)
      alert('Failed to delete contact')
    }
  }

  const getStageColor = (stage: ContactStage) => {
    const colors: Record<ContactStage, 'default' | 'secondary' | 'outline'> = {
      prospect: 'outline',
      interested: 'secondary',
      studying: 'default',
      believer: 'default',
      baptized: 'default',
      disciple: 'outline'
    }
    return colors[stage]
  }

  const getInteractionIcon = (type: string) => {
    const icons: Record<string, React.ReactNode> = {
      visit: <Users className="h-4 w-4" />,
      call: <Phone className="h-4 w-4" />,
      message: <MessageSquare className="h-4 w-4" />,
      bible_study: <BookOpen className="h-4 w-4" />,
      prayer: <Heart className="h-4 w-4" />,
      event: <Calendar className="h-4 w-4" />,
      salvation: <CheckCircle className="h-4 w-4" />
    }
    return icons[type] || <MessageSquare className="h-4 w-4" />
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4 p-4 md:p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Evangelism</h1>
          <p className="text-muted-foreground">
            Manage contacts, track conversions, and coordinate outreach
          </p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)}>
          <UserPlus className="mr-2 h-4 w-4" />
          New Contact
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Contacts</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.totalContacts || 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Souls Won</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{stats?.soulsWon || 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Bible Studies</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.bibleStudies || 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">This Month</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.thisMonth || 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Conversion Rate</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.conversionRate.toFixed(1) || 0}%</div>
          </CardContent>
        </Card>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="contacts">
            <Users className="mr-2 h-4 w-4" />
            Contacts ({contacts.length})
          </TabsTrigger>
          <TabsTrigger value="analytics">
            <BarChart3 className="mr-2 h-4 w-4" />
            Analytics
          </TabsTrigger>
          <TabsTrigger value="overdue">
            <AlertCircle className="mr-2 h-4 w-4" />
            Overdue ({stats?.overdueFollowUps.length || 0})
          </TabsTrigger>
        </TabsList>

        {/* CONTACTS TAB */}
        <TabsContent value="contacts" className="space-y-4 mt-4">
          {/* Filters */}
          <Card>
            <CardContent className="pt-6">
              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label>Search</Label>
                  <div className="relative">
                    <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Name, phone, or email..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-8"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Stage</Label>
                  <Select value={stageFilter} onValueChange={(v) => setStageFilter(v as any)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Stages</SelectItem>
                      <SelectItem value="prospect">Prospect</SelectItem>
                      <SelectItem value="interested">Interested</SelectItem>
                      <SelectItem value="studying">Studying</SelectItem>
                      <SelectItem value="believer">Believer</SelectItem>
                      <SelectItem value="baptized">Baptized</SelectItem>
                      <SelectItem value="disciple">Disciple</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-end">
                  <Button onClick={loadContacts}>
                    <Filter className="mr-2 h-4 w-4" />
                    Apply Filters
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Contacts Table */}
          <Card>
            <CardHeader>
              <CardTitle>Evangelism Contacts</CardTitle>
              <CardDescription>
                All contacts being tracked for evangelism and discipleship
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Contact Info</TableHead>
                    <TableHead>Stage</TableHead>
                    <TableHead>Evangelist</TableHead>
                    <TableHead>Last Contact</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {contacts.length > 0 ? (
                    contacts.map((contact) => (
                      <TableRow
                        key={contact.id}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => handleContactClick(contact)}
                      >
                        <TableCell className="font-medium">{contact.name}</TableCell>
                        <TableCell>
                          <div className="space-y-1 text-sm">
                            {contact.phone && (
                              <div className="flex items-center gap-1">
                                <Phone className="h-3 w-3" />
                                {contact.phone}
                              </div>
                            )}
                            {contact.email && (
                              <div className="flex items-center gap-1">
                                <Mail className="h-3 w-3" />
                                {contact.email}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                              <Badge variant={getStageColor(contact.stage)} className="cursor-pointer">
                                {evangelismService.getStageLabel(contact.stage)}
                              </Badge>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent>
                              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleStageChange(contact.id, 'prospect') }}>
                                Prospect
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleStageChange(contact.id, 'interested') }}>
                                Interested
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleStageChange(contact.id, 'studying') }}>
                                Studying
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleStageChange(contact.id, 'believer') }}>
                                Believer
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleStageChange(contact.id, 'baptized') }}>
                                Baptized
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleStageChange(contact.id, 'disciple') }}>
                                Disciple
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                        <TableCell>
                          {contact.evangelist
                            ? `${contact.evangelist.first_name} ${contact.evangelist.last_name}`
                            : 'Unknown'}
                        </TableCell>
                        <TableCell>
                          {contact.last_interaction
                            ? new Date(contact.last_interaction).toLocaleDateString()
                            : 'Never'}
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => {
                                setSelectedContact(contact)
                                setShowEditDialog(true)
                              }}>
                                <Edit className="mr-2 h-4 w-4" />
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => {
                                setSelectedContact(contact)
                                setShowInteractionDialog(true)
                              }}>
                                <Plus className="mr-2 h-4 w-4" />
                                Record Interaction
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => {
                                setSelectedContact(contact)
                                setShowAssignDialog(true)
                              }}>
                                <UserPlus className="mr-2 h-4 w-4" />
                                Assign Discipler
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => handleDeleteContact(contact.id)}
                                className="text-destructive"
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                        No contacts found. Add your first evangelism contact to get started!
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ANALYTICS TAB */}
        <TabsContent value="analytics" className="space-y-4 mt-4">
          <div className="grid gap-4 md:grid-cols-2">
            {/* Stage Breakdown */}
            <Card>
              <CardHeader>
                <CardTitle>Contacts by Stage</CardTitle>
                <CardDescription>Distribution across the conversion funnel</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(stats?.byStage || {}).map(([stage, count]) => (
                    <div key={stage} className="flex items-center justify-between">
                      <Badge variant="outline">{evangelismService.getStageLabel(stage as ContactStage)}</Badge>
                      <div className="flex items-center gap-2">
                        <div className="text-2xl font-bold">{count}</div>
                        <div className="text-sm text-muted-foreground">
                          ({stats?.totalContacts ? ((count / stats.totalContacts) * 100).toFixed(0) : 0}%)
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Top Evangelists */}
            <Card>
              <CardHeader>
                <CardTitle>Top Evangelists</CardTitle>
                <CardDescription>Leaderboard by souls won</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {stats?.topEvangelists.slice(0, 5).map((evangelist, index) => (
                    <div key={evangelist.id} className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="flex items-center justify-center w-6 h-6 rounded-full bg-muted text-sm font-semibold">
                          {index + 1}
                        </div>
                        <div>
                          <div className="font-medium">{evangelist.name}</div>
                          <div className="text-sm text-muted-foreground">
                            {evangelist.contactsCount} contacts
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Award className="h-4 w-4 text-yellow-600" />
                        <div className="text-lg font-bold">{evangelist.soulsWon}</div>
                      </div>
                    </div>
                  )) || (
                    <p className="text-center text-muted-foreground py-4">No data yet</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Recent Activity */}
          <Card>
            <CardHeader>
              <CardTitle>Recent Activity</CardTitle>
              <CardDescription>Latest evangelism interactions</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {stats?.recentActivity.slice(0, 10).map((interaction) => (
                  <div key={interaction.id} className="flex items-start gap-3 pb-3 border-b last:border-0">
                    <div className="mt-1">{getInteractionIcon(interaction.type)}</div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <div className="font-medium">
                          {evangelismService.getInteractionLabel(interaction.type)}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {new Date(interaction.created_at).toLocaleDateString()}
                        </div>
                      </div>
                      {interaction.notes && (
                        <div className="text-sm text-muted-foreground mt-1">{interaction.notes}</div>
                      )}
                    </div>
                  </div>
                )) || (
                  <p className="text-center text-muted-foreground py-4">No recent activity</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* OVERDUE TAB */}
        <TabsContent value="overdue" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Overdue Follow-ups</CardTitle>
              <CardDescription>Contacts that need immediate attention</CardDescription>
            </CardHeader>
            <CardContent>
              {stats?.overdueFollowUps && stats.overdueFollowUps.length > 0 ? (
                <div className="space-y-3">
                  {stats.overdueFollowUps.map((contact) => (
                    <div
                      key={contact.id}
                      className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 cursor-pointer"
                      onClick={() => handleContactClick(contact)}
                    >
                      <div>
                        <div className="font-medium">{contact.name}</div>
                        <div className="text-sm text-muted-foreground">
                          Next action: {contact.next_action}
                        </div>
                        <div className="text-xs text-red-600">
                          Due: {contact.next_action_date ? new Date(contact.next_action_date).toLocaleDateString() : 'Unknown'}
                        </div>
                      </div>
                      <Badge variant="destructive">Overdue</Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <CheckCircle className="h-12 w-12 mx-auto mb-2 text-green-600" />
                  <p>No overdue follow-ups! You're all caught up.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Contact Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl !bg-white dark:!bg-slate-950 border border-border">
          <form onSubmit={handleCreateContact}>
            <DialogHeader>
              <DialogTitle>New Evangelism Contact</DialogTitle>
              <DialogDescription>
                Add a new contact to track for evangelism and discipleship
              </DialogDescription>
            </DialogHeader>

            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Name *</Label>
                  <Input
                    id="name"
                    required
                    value={newContact.name}
                    onChange={(e) => setNewContact({ ...newContact, name: e.target.value })}
                    placeholder="Full name"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="stage">Stage *</Label>
                  <Select value={newContact.stage} onValueChange={(v) => setNewContact({ ...newContact, stage: v as ContactStage })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="prospect">Prospect</SelectItem>
                      <SelectItem value="interested">Interested</SelectItem>
                      <SelectItem value="studying">Studying</SelectItem>
                      <SelectItem value="believer">Believer</SelectItem>
                      <SelectItem value="baptized">Baptized</SelectItem>
                      <SelectItem value="disciple">Disciple</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={newContact.phone}
                    onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })}
                    placeholder="+1234567890"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={newContact.email}
                    onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                    placeholder="email@example.com"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="evangelist">Assign to Evangelist</Label>
                <Select value={newContact.evangelist_id} onValueChange={(v) => setNewContact({ ...newContact, evangelist_id: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select evangelist (defaults to you)" />
                  </SelectTrigger>
                  <SelectContent>
                    {evangelists.map(e => (
                      <SelectItem key={e.id} value={e.id}>
                        {e.name} ({e.email})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={newContact.notes}
                  onChange={(e) => setNewContact({ ...newContact, notes: e.target.value })}
                  placeholder="Any important information..."
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="next_action">Next Action</Label>
                  <Input
                    id="next_action"
                    value={newContact.next_action}
                    onChange={(e) => setNewContact({ ...newContact, next_action: e.target.value })}
                    placeholder="Call, visit, etc."
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="next_action_date">Next Action Date</Label>
                  <Input
                    id="next_action_date"
                    type="date"
                    value={newContact.next_action_date}
                    onChange={(e) => setNewContact({ ...newContact, next_action_date: e.target.value })}
                  />
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Creating...</> : 'Create Contact'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Contact Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl !bg-white dark:!bg-slate-950 border border-border">
          <DialogHeader>
            <DialogTitle>Edit Contact</DialogTitle>
            <DialogDescription>Update contact information</DialogDescription>
          </DialogHeader>

          {selectedContact && (
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Name</Label>
                  <Input
                    value={selectedContact.name}
                    onChange={(e) => setSelectedContact({ ...selectedContact, name: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Stage</Label>
                  <Select
                    value={selectedContact.stage}
                    onValueChange={(v) => setSelectedContact({ ...selectedContact, stage: v as ContactStage })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="prospect">Prospect</SelectItem>
                      <SelectItem value="interested">Interested</SelectItem>
                      <SelectItem value="studying">Studying</SelectItem>
                      <SelectItem value="believer">Believer</SelectItem>
                      <SelectItem value="baptized">Baptized</SelectItem>
                      <SelectItem value="disciple">Disciple</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input
                    type="tel"
                    value={selectedContact.phone || ''}
                    onChange={(e) => setSelectedContact({ ...selectedContact, phone: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={selectedContact.email || ''}
                    onChange={(e) => setSelectedContact({ ...selectedContact, email: e.target.value })}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={selectedContact.notes || ''}
                  onChange={(e) => setSelectedContact({ ...selectedContact, notes: e.target.value })}
                  rows={3}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button onClick={handleEditContact} disabled={saving}>
              {saving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving...</> : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Record Interaction Dialog */}
      <Dialog open={showInteractionDialog} onOpenChange={setShowInteractionDialog}>
        <DialogContent className="!bg-white dark:!bg-slate-950 border border-border">
          <form onSubmit={handleRecordInteraction}>
            <DialogHeader>
              <DialogTitle>Record Interaction</DialogTitle>
              <DialogDescription>
                Log a new interaction with {selectedContact?.name}
              </DialogDescription>
            </DialogHeader>

            <div className="grid gap-4 py-4">
              <div className="space-y-2">
                <Label>Interaction Type *</Label>
                <Select
                  value={newInteraction.type}
                  onValueChange={(v) => setNewInteraction({ ...newInteraction, type: v as any })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="visit">Visit</SelectItem>
                    <SelectItem value="call">Phone Call</SelectItem>
                    <SelectItem value="message">Message</SelectItem>
                    <SelectItem value="bible_study">Bible Study</SelectItem>
                    <SelectItem value="prayer">Prayer</SelectItem>
                    <SelectItem value="event">Event</SelectItem>
                    <SelectItem value="salvation">Salvation</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Duration (minutes)</Label>
                <Input
                  type="number"
                  value={newInteraction.duration_minutes}
                  onChange={(e) => setNewInteraction({ ...newInteraction, duration_minutes: parseInt(e.target.value) || 0 })}
                  placeholder="30"
                />
              </div>

              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={newInteraction.notes}
                  onChange={(e) => setNewInteraction({ ...newInteraction, notes: e.target.value })}
                  placeholder="What was discussed, outcomes, prayer points..."
                  rows={4}
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowInteractionDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Recording...</> : 'Record Interaction'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Assign Discipler Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent className="!bg-white dark:!bg-slate-950 border border-border">
          <DialogHeader>
            <DialogTitle>Assign Discipler</DialogTitle>
            <DialogDescription>
              Assign someone to disciple {selectedContact?.name}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label>Select Discipler *</Label>
              <Select value={selectedDiscipler} onValueChange={setSelectedDiscipler}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a discipler..." />
                </SelectTrigger>
                <SelectContent>
                  {evangelists.map(e => (
                    <SelectItem key={e.id} value={e.id}>
                      {e.name} ({e.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignDialog(false)}>Cancel</Button>
            <Button onClick={handleAssignDiscipler} disabled={saving || !selectedDiscipler}>
              {saving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Assigning...</> : 'Assign Discipler'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Contact Detail Dialog */}
      <Dialog open={showContactDialog} onOpenChange={setShowContactDialog}>
        <DialogContent className="max-w-2xl !bg-white dark:!bg-slate-950 border border-border">
          <DialogHeader>
            <DialogTitle>{selectedContact?.name}</DialogTitle>
            <DialogDescription>Contact details and interaction timeline</DialogDescription>
          </DialogHeader>

          {selectedContact && (
            <div className="space-y-4 py-4">
              {/* Contact Info */}
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <Label>Stage</Label>
                  <div className="mt-2">
                    <Badge variant={getStageColor(selectedContact.stage)}>
                      {evangelismService.getStageLabel(selectedContact.stage)}
                    </Badge>
                  </div>
                </div>
                <div>
                  <Label>Evangelist</Label>
                  <div className="mt-2">
                    {selectedContact.evangelist
                      ? `${selectedContact.evangelist.first_name} ${selectedContact.evangelist.last_name}`
                      : 'Unknown'}
                  </div>
                </div>
              </div>

              {/* Interaction Timeline */}
              <div>
                <Label>Interaction Timeline ({contactInteractions.length})</Label>
                <div className="mt-2 space-y-2 max-h-[300px] overflow-y-auto">
                  {contactInteractions.length > 0 ? (
                    contactInteractions.map((interaction) => (
                      <div key={interaction.id} className="flex items-start gap-2 p-2 border rounded">
                        {getInteractionIcon(interaction.type)}
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <div className="font-medium text-sm">
                              {evangelismService.getInteractionLabel(interaction.type)}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {new Date(interaction.created_at).toLocaleDateString()}
                            </div>
                          </div>
                          {interaction.notes && (
                            <div className="text-sm text-muted-foreground">{interaction.notes}</div>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No interactions recorded yet
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowContactDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
